import { Server, Copy, Check, Cpu, Network, Terminal, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useRunners, useRunnerInstallToken } from '../hooks/useRunners';
import RunnerCard from '../components/RunnerCard';

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
      title="Copy to clipboard"
    >
      {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
    </button>
  );
}

function RunnerSetup() {
  const { mutate: generateToken, data: tokenData, isPending, reset } = useRunnerInstallToken();
  const [activeTab, setActiveTab] = useState<'oneliner' | 'npm'>('oneliner');
  const SERVER_URL = window.location.origin;

  const npmConfigLines = [
    `npm config set @aldan-mcp:registry ${SERVER_URL}/api/npm-registry/`,
    `npm config set //${window.location.host}/:_authToken YOUR_API_KEY`,
    'npm install -g @aldan-mcp/host-runner',
    'aldan login',
    'aldan start',
  ];

  return (
    <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-6">
      <h2 className="text-sm font-semibold text-slate-900">Connect a Runner</h2>
      <p className="mt-1 text-xs text-slate-500">
        Install the host runner on any machine to execute Claude CLI instances remotely.
      </p>

      <div className="mt-4 flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('oneliner')}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'oneliner'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Terminal className="h-3.5 w-3.5" />
          One-liner install
        </button>
        <button
          onClick={() => setActiveTab('npm')}
          className={`flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
            activeTab === 'npm'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          npm registry
        </button>
      </div>

      {activeTab === 'oneliner' && (
        <div className="mt-4 space-y-3">
          <p className="text-xs text-slate-500">
            Generate a one-time install command. It will pre-create an API key and configure the runner automatically.
          </p>
          {tokenData ? (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <code className="flex-1 break-all text-xs text-emerald-800">{tokenData.installCommand}</code>
                  <CopyButton text={tokenData.installCommand} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-400">
                  Expires {new Date(tokenData.expiresAt).toLocaleTimeString()} · API key: <span className="font-mono">{tokenData.apiKeyName}</span>
                </p>
                <button
                  onClick={() => reset()}
                  className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
                >
                  <RefreshCw className="h-3 w-3" />
                  New token
                </button>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-xs text-slate-600 space-y-1">
                <p className="font-medium text-slate-700">After running the command above:</p>
                <p>• The runner is installed and your API key is saved automatically</p>
                <p>• Run <code className="rounded bg-slate-100 px-1">aldan start</code> to connect</p>
                <p>• To reconfigure later, run <code className="rounded bg-slate-100 px-1">aldan login</code></p>
              </div>
            </div>
          ) : (
            <button
              onClick={() => generateToken()}
              disabled={isPending}
              className="flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {isPending ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Terminal className="h-3.5 w-3.5" />
              )}
              Generate install command
            </button>
          )}
        </div>
      )}

      {activeTab === 'npm' && (
        <div className="mt-4 space-y-2">
          <p className="text-xs text-slate-500">
            Configure npm once to use this server&apos;s private registry. Replace{' '}
            <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">YOUR_API_KEY</code> with a key
            from Settings → API Keys. Steps 1–2 are one-time setup per machine.
          </p>
          {npmConfigLines.map((line, i) => (
            <div key={i} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
              <code className="flex-1 text-xs text-slate-700">{line}</code>
              <CopyButton text={line} />
            </div>
          ))}
          <p className="text-xs text-slate-400 pt-1">
            <code className="rounded bg-slate-100 px-1">aldan login</code> will prompt for your server URL and API key,
            validate them, and save to <code className="rounded bg-slate-100 px-1">~/.aldan/config.json</code>.
          </p>
        </div>
      )}
    </div>
  );
}

type Tab = 'runners' | 'peers';

export default function Runners() {
  const { data: runners, isLoading } = useRunners();
  const [tab, setTab] = useState<Tab>('runners');

  const onlineCount = (runners ?? []).filter((r) => r.status === 'online').length;
  const llmRunners = (runners ?? []).filter((r) => r.capabilities?.includes('local-llm'));
  const peerRunners = (runners ?? []).filter((r) => r.capabilities?.includes('p2p'));

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Runners</h1>
          <p className="mt-1 text-sm text-slate-500">
            {runners && runners.length > 0
              ? `${runners.length} runner${runners.length !== 1 ? 's' : ''} registered, ${onlineCount} online`
              : 'Manage your host runners'}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-1 border-b border-slate-200">
        <button
          onClick={() => setTab('runners')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            tab === 'runners'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Server className="h-4 w-4" />
          Runners
          {runners && runners.length > 0 && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{runners.length}</span>
          )}
        </button>
        <button
          onClick={() => setTab('peers')}
          className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
            tab === 'peers'
              ? 'border-slate-900 text-slate-900'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          <Network className="h-4 w-4" />
          P2P Peers
          {peerRunners.length > 0 && (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">{peerRunners.length}</span>
          )}
        </button>
      </div>

      {tab === 'runners' && (
        <>
          {isLoading ? (
            <div className="text-sm text-slate-400">Loading runners...</div>
          ) : runners && runners.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {runners.map((runner) => (
                  <RunnerCard key={runner.id} runner={runner} />
                ))}
              </div>

              {/* Local LLM models section */}
              {llmRunners.length > 0 && (
                <div className="mt-8 rounded-xl border border-purple-200 bg-purple-50/50 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Cpu className="h-5 w-5 text-purple-600" />
                    <h2 className="text-sm font-semibold text-slate-900">Local LLM Runners</h2>
                  </div>
                  <p className="text-xs text-slate-500 mb-4">
                    Runners with local-llm capability can execute instances using locally-hosted models.
                  </p>
                  <div className="space-y-2">
                    {llmRunners.map((runner) => (
                      <div
                        key={runner.id}
                        className="flex items-center justify-between rounded-lg border border-purple-200 bg-white px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`h-2 w-2 rounded-full ${runner.status === 'online' ? 'bg-green-500' : 'bg-slate-300'}`} />
                          <div>
                            <div className="text-sm font-medium text-slate-900">{runner.name}</div>
                            <div className="text-xs text-slate-400">{runner.hostname}</div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {runner.capabilities
                            .filter((c) => c !== 'cli')
                            .map((cap) => (
                              <span
                                key={cap}
                                className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-medium text-purple-700"
                              >
                                {cap}
                              </span>
                            ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
              <Server className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">No runners registered yet.</p>
              <p className="mt-1 text-xs text-slate-400">
                Connect a runner to execute instances on remote machines.
              </p>
            </div>
          )}

          <RunnerSetup />
        </>
      )}

      {tab === 'peers' && (
        <>
          {peerRunners.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {peerRunners.map((runner) => (
                <RunnerCard key={runner.id} runner={runner} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
              <Network className="mx-auto mb-3 h-10 w-10 text-slate-300" />
              <p className="text-sm text-slate-500">No P2P peers registered.</p>
              <p className="mt-1 text-xs text-slate-400">
                P2P peers are desktop clients running cortex-desktop with peer-to-peer enabled.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
